/**
 * @develio/auth/client — React / browser-side Clerk wrappers.
 *
 * Imports from `@clerk/clerk-react` and re-exports a Develio-branded API.
 * Must never be imported from server code — `@clerk/clerk-react` assumes a
 * browser environment and will explode in Node.
 *
 * See ADR-007. The rule: apps/web and apps/intake ONLY import from
 * `@develio/auth/client`, never from `@clerk/clerk-react` directly.
 */

import {
  ClerkProvider,
  useAuth,
  useOrganization,
  useOrganizationList,
  useUser,
} from '@clerk/clerk-react';
import { type ReactNode, useCallback, useEffect } from 'react';
import { type Role, clerkRoleToDevelioRole, roleSatisfies } from './index.js';

/**
 * Mount this inside <ClerkProvider> to auto-activate the user's first org
 * if no org is currently active in the session. Fixes the case where a user
 * has an org membership but the JWT lacks `org_id`.
 */
export function OrgAutoActivator() {
  const { isLoaded: authLoaded, orgId } = useAuth();
  const {
    isLoaded: listLoaded,
    userMemberships,
    setActive,
  } = useOrganizationList({
    userMemberships: { infinite: true },
  });

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[OrgAutoActivator]', {
      authLoaded,
      listLoaded,
      orgId,
      memberships: userMemberships?.data?.length,
      firstOrg: userMemberships?.data?.[0]?.organization?.id,
    });
    if (!authLoaded || !listLoaded || orgId || !setActive) return;
    const first = userMemberships?.data?.[0]?.organization;
    if (first) {
      // eslint-disable-next-line no-console
      console.log('[OrgAutoActivator] calling setActive', first.id);
      void setActive({ organization: first.id }).then(
        () => console.log('[OrgAutoActivator] setActive done'),
        (err) => console.error('[OrgAutoActivator] setActive failed', err),
      );
    }
  }, [authLoaded, listLoaded, orgId, userMemberships, setActive]);

  return null;
}

/* Direct re-exports — no aliasing, keeps TS declaration emission portable. */
export {
  SignedIn,
  SignedOut,
  UserButton,
  OrganizationSwitcher,
  SignIn,
  SignUp,
} from '@clerk/clerk-react';

/**
 * ClerkProvider wrapper — a Sprint 6 refinement may add a loading boundary or
 * Sentry integration here. For now it's a pure passthrough.
 */
export interface DevelioAuthProviderProps {
  readonly publishableKey: string;
  readonly children: ReactNode;
}

export function DevelioAuthProvider({ publishableKey, children }: DevelioAuthProviderProps) {
  return <ClerkProvider publishableKey={publishableKey}>{children}</ClerkProvider>;
}

/**
 * Hook returning the current user in Develio-branded shape. Returns `null`
 * while Clerk is still loading OR when the user is signed out.
 */
export interface CurrentUserSummary {
  readonly clerkUserId: string;
  readonly email: string | null;
  readonly fullName: string | null;
  readonly imageUrl: string | null;
}

export function useCurrentUser(): CurrentUserSummary | null {
  const { isLoaded, isSignedIn, user } = useUser();
  if (!isLoaded || !isSignedIn || !user) return null;
  return {
    clerkUserId: user.id,
    email: user.primaryEmailAddress?.emailAddress ?? null,
    fullName: user.fullName ?? null,
    imageUrl: user.imageUrl ?? null,
  };
}

/**
 * Hook returning the current active organization and the user's role within it.
 * Returns `null` while loading, when no org is active, or when signed out.
 */
export interface CurrentOrgSummary {
  readonly clerkOrgId: string;
  readonly name: string;
  readonly slug: string | null;
  readonly role: Role;
}

export function useCurrentOrg(): CurrentOrgSummary | null {
  const { isLoaded, organization, membership } = useOrganization();
  if (!isLoaded || !organization || !membership) return null;
  return {
    clerkOrgId: organization.id,
    name: organization.name,
    slug: organization.slug,
    role: clerkRoleToDevelioRole(membership.role),
  };
}

/**
 * Hook returning the Clerk session token for authenticated API calls.
 * Returns an async getter because Clerk refreshes tokens in the background.
 */
export function useSessionTokenGetter(): () => Promise<string | null> {
  const { getToken } = useAuth();
  return useCallback(async () => {
    try {
      return await getToken();
    } catch {
      return null;
    }
  }, [getToken]);
}

/**
 * Conditional render component — shows children only when the current user
 * satisfies the required role. Renders `fallback` (or nothing) otherwise.
 *
 * Prop is named `requiredRole` rather than `role` to avoid collision with
 * the HTML ARIA `role` attribute in static analysis tools (Biome, eslint-plugin-jsx-a11y).
 */
export interface RequireRoleProps {
  readonly requiredRole: Role;
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

export function RequireRole({ requiredRole, children, fallback = null }: RequireRoleProps) {
  const org = useCurrentOrg();
  if (!org) return <>{fallback}</>;
  if (!roleSatisfies(org.role, requiredRole)) return <>{fallback}</>;
  return <>{children}</>;
}
